import { useState, useMemo } from 'react'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { cn } from '@/lib/utils'
import { NumberInputField } from './atoms/NumberInputField'
import { DynamicRowList } from './molecules/DynamicRowList'
import { calcularCPAMaximo } from '../lib/cpaCalculations'
import { useRaioXState } from '../hooks/useRaioXState'
import { cpaMaximoDefaultValues } from '../constants'
import type { VariableCost, CalculatorResult } from '../types'

export function CpaMaximoPage() {
  const { values, updateValue } = useRaioXState(cpaMaximoDefaultValues)
  const [custosVariaveis, setCustosVariaveis] = useState<VariableCost[]>([])

  const results = useMemo(() => {
    return calcularCPAMaximo({ ...values, custosVariaveis })
  }, [values, custosVariaveis])

  return (
    <div className="flex flex-col gap-6 max-w-[900px] mx-auto">
      <div>
        <h2 className="text-xl font-semibold tracking-[-0.022em] text-foreground">CPA Máximo</h2>
        <p className="text-sm text-muted-foreground mt-0.5">
          Calcule o teto de custo por aquisição para manter a operação saudável
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
        {/* Inputs */}
        <Card className="bg-card border border-border rounded-2xl">
          <CardHeader className="pb-4 pt-5 px-5">
            <CardTitle className="text-sm font-semibold tracking-[-0.022em]">Parâmetros</CardTitle>
          </CardHeader>
          <CardContent className="px-5 pb-5 space-y-4">
            <NumberInputField
              id="precoProduto"
              label="Preço do Produto"
              prefix="R$"
              value={values.precoProduto}
              onChange={(v) => updateValue('precoProduto', v)}
            />
            <NumberInputField
              id="metaVendas"
              label="Meta de Vendas"
              value={values.metaVendas}
              onChange={(v) => updateValue('metaVendas', v)}
            />
            <NumberInputField
              id="taxaPlataformaPercent"
              label="Taxa da Plataforma (%)"
              suffix="%"
              value={values.taxaPlataformaPercent}
              onChange={(v) => updateValue('taxaPlataformaPercent', v)}
            />
            <NumberInputField
              id="taxaPlataformaFixo"
              label="Taxa Fixa da Plataforma"
              prefix="R$"
              value={values.taxaPlataformaFixo}
              onChange={(v) => updateValue('taxaPlataformaFixo', v)}
            />

            <div className="pt-2">
              <p className="text-xs font-medium text-muted-foreground mb-2">Custos Variáveis</p>
              <DynamicRowList rows={custosVariaveis} onChange={setCustosVariaveis} />
            </div>
          </CardContent>
        </Card>

        {/* Results */}
        <ResultsCard results={results} />
      </div>
    </div>
  )
}

function ResultsCard({ results }: { results: CalculatorResult[] }) {
  return (
    <Card className="bg-card border border-border rounded-2xl overflow-hidden">
      <div className="flex items-center px-4 py-2.5 border-b border-border/60">
        <span className="text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">
          Resultados
        </span>
      </div>
      <div className="px-4 py-1">
        {results.map((r, i) => (
          <div key={i} className="flex items-center justify-between py-2.5 border-b border-border/50 last:border-0">
            <span className="text-xs text-muted-foreground">{r.label}</span>
            <span
              className={cn(
                'text-sm font-semibold tabular-nums font-mono',
                r.status === 'success' && 'text-success',
                r.status === 'warning' && 'text-warning',
                r.status === 'danger' && 'text-destructive',
                (!r.status || r.status === 'accent') && 'text-foreground',
              )}
            >
              {r.formattedValue}
            </span>
          </div>
        ))}
      </div>
    </Card>
  )
}
